import { useCallback } from "react";
import { Maybe, Organization } from "types";
import { useOne } from "@refinedev/core";
import { useSession } from "./useSession";
import { useAuthActions } from "./useAuthActions";

/**
 * Organization hook - Delegates to useSession and useAuthActions
 * Maintains backward compatibility with existing API
 *
 * @deprecated This hook is maintained for backward compatibility.
 * New code should use useSession() and useAuthActions() directly.
 */
export const useOrganization = ({ id }: { id?: string } = {}) => {
  // Use new unified hooks
  const {
    user,
    organizations,
    currentOrganization,
    isAuthenticated,
    isLoading,
  } = useSession();

  const {
    selectOrganization: changeOrganization,
    createOrganization,
    isCreatingOrg,
  } = useAuthActions();

  const hasMultipleOrganizations = organizations?.length > 1;

  const getOrganization = useCallback(
    (id: string): Maybe<Organization> =>
      organizations?.find((x) => x.id === id) ?? null,
    [organizations]
  );

  /**
   * @deprecated setOrganizations is no longer needed with React Query.
   * Organizations are automatically synced from the server.
   */
  const setOrganizations = useCallback(() => {
    console.warn(
      "setOrganizations is deprecated - use React Query invalidation instead"
    );
  }, []);

  const organization = id ? getOrganization(id) : currentOrganization;

  const { data: organizationPreferences, isLoading: preferencesLoading } =
    useOne({
      resource: "media/preferences",
      id: organization?.id,
      queryOptions: {
        enabled: !!organization?.id && !!isAuthenticated,
        // don't refetch for each new page
        // on settings update it will be invalidated in the org settings page
        refetchOnMount: false,
      },
    });

  return {
    currentOrganization,
    organization,
    getOrganization,
    organizations,
    hasMultipleOrganizations,
    changeOrganization,
    setOrganizations,
    createOrganization,
    isCreating: isCreatingOrg,
    isLoadingLanguage: isLoading, // Map isLoading to isLoadingLanguage for compatibility
    user,
    organizationPreferences: organizationPreferences?.data,
    preferencesLoading,
  };
};
